import { cloneElement } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { FormError, Input } from '@change-corgi/design-system-legacy/components/forms';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';

import type { WithTokenParams } from 'src/app/shared/types';
import type { PaymentMethodSaveOptions, PaymentType } from 'src/app/shared/utils/payments';

import type { SubmitButtonProps } from '../../../index';

import { IbanElementWrapper } from './components/IbanElementWrapper';
import { useIbanForm } from './hooks/useIbanForm';

type Props = {
	name: string;
	email: string;
	paymentMethodSaveOptions: PaymentMethodSaveOptions;
	beforeToken: (paymentType: PaymentType) => Promise<boolean>;
	validate: (paymentType: PaymentType) => Promise<boolean>;
	prePaymentChecks: (paymentType: PaymentType) => Promise<boolean>;
	onTokenError: (paymentType: PaymentType, err: Error) => void;
	onTokenInvalid: (paymentType: PaymentType, err: Error) => void;
	withToken: (params: WithTokenParams) => Promise<void>;
	onFieldFocus: () => void;
	submitButton: React.ReactElement<SubmitButtonProps>;
	isSunriseEnabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export function IbanForm({
	name,
	email,
	paymentMethodSaveOptions,
	beforeToken,
	validate,
	prePaymentChecks,
	onTokenError,
	onTokenInvalid,
	withToken,
	onFieldFocus,
	submitButton,
	isSunriseEnabled,
}: Props): React.JSX.Element | null {
	const { translate } = useI18n();
	const {
		data: { validationError, errorMessage },
		actions: { handleSubmit, validateStripeElementOnChange },
	} = useIbanForm({
		name,
		email,
		paymentMethodSaveOptions,
		beforeToken,
		validate,
		prePaymentChecks,
		onTokenError,
		onTokenInvalid,
		withToken,
	});

	return (
		<>
			<Box>
				<form data-testid="sepa-form" onSubmit={handleSubmit}>
					<Box
						mb={16}
						sx={{
							...(isSunriseEnabled && {
								'& label span': {
									fontSize: 12,
									fontWeight: 'bold',
									color: 'sunrise.semantic.typography.strong',
								},
								'& [id$=___error] > span': {
									marginLeft: '-4px',
									color: 'sunrise.semantic.typography.error',
								},
								'& [id$=___error] span svg': {
									marginRight: '4px',
									color: 'sunrise.semantic.icon.error',
								},
							}),
						}}
					>
						<Input
							className="iframe-form-element"
							// TODO: this is here for the regression-qaa tests. Migrate the tests to use the data-qa attribute.

							data-qa="sepa-iframe-form-element"
							label={translate('fe.components.sepa.account_number')}
							name="account_number"
							error={validationError}
							as={IbanElementWrapper}
							onFocus={onFieldFocus}
							onChange={validateStripeElementOnChange}
							id="sepa-account-number"
							sx={{
								...(isSunriseEnabled && {
									borderColor: 'sunrise.semantic.stroke.weaker',
									borderWidth: 1,
									borderStyle: 'solid',
									'&:hover, &:active, &:focus': {
										borderColor: 'sunrise.semantic.stroke.accent.primary.strong',
										boxShadow: 'none',
									},
									'&:active, &:focus': {
										borderWidth: 2,
									},
									'&[data-error=true]': {
										borderWidth: 1,
										borderStyle: 'solid',
										borderColor: 'sunrise.semantic.stroke.error.weak',
										boxShadow: 'none',
									},
								}),
							}}
						/>
					</Box>
					<Flex
						sx={{
							justifyContent: 'center',
						}}
					>
						{cloneElement(submitButton, {
							disabled: submitButton.props.disabled,
						})}
					</Flex>
				</form>
			</Box>
			{errorMessage && (
				<FormError mb={16} data-qa="iban-form-error-message">
					{errorMessage}
				</FormError>
			)}
		</>
	);
}
